import React, { FC, useMemo, useTransition } from "react";
import classNames from "classnames";
import { Menu } from "types/Menu";

import { getSizeClasses } from "composants/common";
import "./ExpertMenu.css";
import { Fa } from "composants/Icon";
import { t } from "utils/i18n";
import { Menu as DropdownMenu } from "composants/DropDown/Menu";
import {
  useLocation,
  useNavigate,
  useHref,
  useLinkClickHandler,
  LinkProps,
} from "react-router-dom";
import { Button } from "composants/button";
import { track } from "tracking";

interface ExpertMenuProps {
  width?: string;
  className?: string;
  menu: Menu[];
  size?: string; // small | medium | large
  disabled: boolean;
}

type ExpertMenuAllProps = ExpertMenuProps & {
  sjmoCode: string;
  onOpenExpert: () => void;
};

function sortedMenuEntries(menus: Menu[]): Menu[] {
  // ignore les entrées sans niveau (pas rattachées au menu expert), tri stable par niveau
  return menus
    .filter((menu) => menu.level != null)
    .sort((a, b) => (a.level as number) - (b.level as number));
}

const ExpertMenu: FC<ExpertMenuAllProps> = (props) => {
  const { className } = props;
  const sizeClass = getSizeClasses(props.size);

  const menuEntries = useMemo(() => {
    return sortedMenuEntries(props.menu);
  }, [props.menu]);

  // largeur du panel proportionnelle au nombre de rubriques, 5 colonnes max
  const columnCount = Math.min(menuEntries.length, 5);

  const location = useLocation();
  const navigate = useNavigate();

  return (
    <>
      {menuEntries.length > 0 && (
        <DropdownMenu
          autoclose
          className={classNames(className, sizeClass)}
          disabled={props.disabled}
        >
          <DropdownMenu.Button
            className={classNames("button", "is-outlined", "is-link", sizeClass)}
            title={t("commun_expert")}
            disabled={props.disabled}
          >
            <span className="icon">
              <Fa icon="pencil" />
            </span>
          </DropdownMenu.Button>
          <DropdownMenu.Overlay
            className="expert-menu-overlay"
            style={{ "--expert-menu-cols": columnCount } as React.CSSProperties}
          >
            {menuEntries.map((menu) => {
              return (
                <ExpertMenuGroup
                  key={menu.code}
                  code={menu.code}
                  label={menu.label}
                  onOpenExpert={props.onOpenExpert}
                >
                  {menu.children.map((subMenu) => {
                    return (
                      <ExpertMenuItem
                        key={subMenu.code}
                        code={subMenu.code}
                        label={subMenu.label}
                        onOpenExpert={props.onOpenExpert}
                      />
                    );
                  })}
                </ExpertMenuGroup>
              );
            })}
          </DropdownMenu.Overlay>
        </DropdownMenu>
      )}
      {menuEntries.length === 0 && (
        <Button
          title={t("commun_expert")}
          outlined
          color="link"
          onClick={() => {
            navigate(location.pathname + "/expert");
            track("galaxy::expert::open");
          }}
          disabled={props.disabled}
        >
          <span className="icon">
            <Fa icon="pencil" />
          </span>
        </Button>
      )}
    </>
  );
};

function ExpertLink({ to, relative, replace, state, target, onClick, ...rest }: LinkProps) {
  const href = useHref(to, { relative });
  let handleClick = useLinkClickHandler(to, {
    replace,
    state,
    target,
  });
  const [, startTransition] = useTransition();

  function internalOnClick(e: React.MouseEvent<HTMLAnchorElement>) {
    onClick?.(e);
    if (!e.defaultPrevented) {
      startTransition(() => {
        handleClick(e);
      });
    }
  }

  return <a {...rest} href={href} onClick={internalOnClick} target={target} />;
}

// Une rubrique : l'entête ouvre le bloc, les sous-écrans sont listés en dessous
const ExpertMenuGroup: FC<
  React.PropsWithChildren<{
    code: string;
    label: string;
    onOpenExpert: () => void;
  }>
> = ({ code, label, onOpenExpert, children }) => {
  const location = useLocation();
  const urlEncoded = encodeURIComponent(code);

  return (
    <div className="expert-menu-group" key={code}>
      <ExpertLink
        className="expert-menu-group-title"
        to={`${location.pathname}/expert/${urlEncoded}`}
        onClick={() => {
          onOpenExpert();
        }}
      >
        <span>{label}</span>
      </ExpertLink>
      {children}
    </div>
  );
};

// Un sous-écran de la rubrique.
const ExpertMenuItem: FC<{ code: string; label: string; onOpenExpert(): void }> = ({
  code,
  label,
  onOpenExpert,
}) => {
  const location = useLocation();
  const urlEncoded = encodeURIComponent(code);

  return (
    <ExpertLink
      className="expert-menu-item"
      to={`${location.pathname}/expert/${urlEncoded}`}
      onClick={() => {
        onOpenExpert();
      }}
    >
      <span>{label}</span>
    </ExpertLink>
  );
};

export default ExpertMenu;
